import { methodResult, post } from "./api.js";
import { config, labels } from "./config.js";
import {
	setActionLabel,
	setBusy,
	showFieldError,
	showNotice,
	validateEmail,
} from "./components.js";
import { takeEmailHandoff } from "./email-handoff.js";
import { handleLoginPayload } from "./session.js";

export function initLogin() {
	const form = document.querySelector("[data-auth-login-form]");
	if (!form) return;
	const email = form.elements.email;
	const password = form.elements.password;
	const passwordField = password.closest("[data-auth-field]");
	let resolvedEmail = "";
	let authMethod = "password";
	let resolutionVersion = 0;
	let pendingResolution = null;
	let pendingEmail = "";
	const handedOffEmail = takeEmailHandoff();
	if (handedOffEmail && !email.value) {
		email.value = handedOffEmail;
		password.focus({ preventScroll: true });
	}

	function resetResolution() {
		resolutionVersion += 1;
		resolvedEmail = "";
		authMethod = "password";
		passwordField.hidden = false;
		setActionLabel(form, labels.login);
		showNotice(form, "");
	}

	async function resolveAuthentication() {
		const candidate = email.value.trim().toLowerCase();
		if (candidate === resolvedEmail) return authMethod;
		if (candidate === pendingEmail && pendingResolution) return pendingResolution;

		const requestVersion = resolutionVersion;
		pendingEmail = candidate;
		pendingResolution = post(
			"/api/method/syscofy_core.api.web_auth_registration.resolve_email_authentication",
			{ email: candidate },
		).then((payload) => {
			const result = methodResult(payload);
			if (!result?.ok) throw new Error(result?.error?.message || labels.requestFailed);
			if (requestVersion !== resolutionVersion) return authMethod;

			resolvedEmail = candidate;
			if (result.status === "existing_user" && result.auth_method === "google") {
				authMethod = "google";
				passwordField.hidden = true;
				setActionLabel(form, labels.continueGoogle);
				showNotice(form, result.message, "success");
				return authMethod;
			}
			if (["registration_pending", "disabled_user"].includes(result.status)) {
				authMethod = "blocked";
				passwordField.hidden = true;
				showNotice(form, result.message);
				return authMethod;
			}
			authMethod = "password";
			return authMethod;
		});
		try {
			return await pendingResolution;
		} finally {
			if (pendingEmail === candidate) {
				pendingEmail = "";
				pendingResolution = null;
			}
		}
	}

	email.addEventListener("input", resetResolution);
	email.addEventListener("blur", () => {
		if (!validateEmail(email, { allowAdministrator: true })) {
			resolveAuthentication().catch(() => {});
		}
	});
	form.addEventListener("submit", async (event) => {
		event.preventDefault();
		showNotice(form, "");
		const emailError = validateEmail(email, { allowAdministrator: true });
		if (emailError) {
			showFieldError(email, emailError);
			return;
		}
		setBusy(form, true, labels.verifying);
		try {
			let method = authMethod;
			try {
				method = await resolveAuthentication();
			} catch (_error) {
				method = "password";
			}
			if (method === "google") {
				location.assign(config.googleUrl);
				return;
			}
			if (method === "blocked") return;
			if (!password.value) {
				showFieldError(password, labels.passwordRequired);
				return;
			}
			const payload = await post("/api/method/login", {
				usr: email.value.trim(),
				pwd: password.value,
			});
			if (!handleLoginPayload(payload)) showNotice(form, labels.invalidCredentials);
		} catch (error) {
			showNotice(
				form,
				error.status === 429 ? labels.tooManyRequests : labels.invalidCredentials,
			);
		} finally {
			setBusy(form, false, labels.verifying);
		}
	});
}
